import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Input, Table, Tag } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { createLmsApi, getLmsErrorMessage, getLmsListItems } from "../../lms/api";
import { LmsErrorAlert, LmsPageHeader, formatDateTime } from "../../lms/components/LmsPrimitives";

const LmsUploadLogs = () => {
  const navigate = useNavigate();
  const api = useMemo(() => createLmsApi(navigate), [navigate]);
  const [groups, setGroups] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const pageSize = pagination.pageSize;

  const loadLogs = useCallback(async (page = 1, pageSize = 20) => {
    setLoading(true);
    setError("");
    const response = await api.contentUploadLog({ page, pageSize, search: search || undefined });
    if (response?.ok) {
      const result = getLmsListItems(response.data);
      setGroups(result.items);
      setPagination({ current: page, pageSize, total: result.count });
    } else setError(getLmsErrorMessage(response, "Unable to load upload logs"));
    setLoading(false);
  }, [api, search]);

  useEffect(() => { loadLogs(1, pageSize); }, [loadLogs, pageSize]);

  const logColumns = [
    { title: "Teacher", dataIndex: "teacherName", key: "teacherName", width: 200 },
    { title: "Class", dataIndex: "className", key: "className", width: 130 },
    { title: "Stream", dataIndex: "streamName", key: "streamName", width: 120, render: (value) => value || "—" },
    { title: "Subject", dataIndex: "subjectName", key: "subjectName", width: 180 },
    { title: "Week", dataIndex: "week_number", key: "week_number", width: 80 },
    { title: "Uploads", dataIndex: "total_uploads", key: "total_uploads", width: 100, render: (value) => <Tag color="blue">{value}</Tag> },
    { title: "Last uploaded", dataIndex: "lastUploadedAt", key: "lastUploadedAt", width: 190, render: (value) => formatDateTime(value) },
  ];

  const columns = [
    { title: "Session", dataIndex: "sessionTitle", key: "sessionTitle", width: 260, render: (value) => <span style={{ whiteSpace: "normal", overflowWrap: "anywhere", fontWeight: 600 }}>{value}</span> },
    { title: "Teachers", dataIndex: "teacherCount", key: "teacherCount", width: 110 },
    { title: "Weeks", dataIndex: "weekCount", key: "weekCount", width: 90 },
    { title: "Uploads", dataIndex: "totalUploads", key: "totalUploads", width: 110, render: (value) => <Tag color="blue">{value}</Tag> },
    { title: "Last uploaded", dataIndex: "lastUploadedAt", key: "lastUploadedAt", width: 190, render: (value) => formatDateTime(value) },
  ];

  return (
    <div style={{ padding: 24 }}>
      <LmsPageHeader title="Content upload logs" description="Read-only history of teacher uploads, grouped by session, assignment and week." extra={<Button icon={<ReloadOutlined />} loading={loading} onClick={() => loadLogs(pagination.current, pagination.pageSize)}>Refresh</Button>} />
      <LmsErrorAlert error={error} onClose={() => setError("")} />
      <Card>
        <Input.Search allowClear placeholder="Search upload logs" value={search} onChange={(event) => setSearch(event.target.value)} onSearch={() => loadLogs(1, pagination.pageSize)} style={{ maxWidth: 360, marginBottom: 16 }} />
        <Table
          rowKey="sessionID"
          columns={columns}
          dataSource={groups}
          loading={loading}
          tableLayout="fixed"
          scroll={{ x: 800 }}
          pagination={{ ...pagination, showSizeChanger: false, showTotal: (total) => `${total} session${total === 1 ? "" : "s"}` }}
          onChange={(nextPagination) => loadLogs(nextPagination.current, nextPagination.pageSize)}
          expandable={{
            rowExpandable: (record) => Array.isArray(record.logs) && record.logs.length > 0,
            expandedRowRender: (record) => (
              <Table
                rowKey="id"
                columns={logColumns}
                dataSource={record.logs || []}
                pagination={false}
                size="small"
                tableLayout="fixed"
                scroll={{ x: 900 }}
              />
            ),
          }}
        />
      </Card>
    </div>
  );
};

export default LmsUploadLogs;
